import type {
  HubbleExperiments,
  TealiumExperimentProps,
} from '@seek/candidate-feature-flag';
import type { Hubble } from '@seek/hubble';

import type { TrackingLicenceAttributes } from 'src/components/Licences/hooks/tracking';
import type { TrackingCertsyAttributes } from 'src/pages/Success/Certsy/Certsy';
import type { TrackingLmisAttributes } from 'src/pages/Success/Lmis/Lmis';
import type { Brand, Language, Locale, Zone } from 'src/types';

interface SetPageArgs {
  pageTitle: string;
  currentPage: string;
}

export type SetPage = (payload: SetPageArgs) => void;

export type TrackPageView = (pageTitle: string, extra?: any) => void;

export enum DocumentType {
  Resume = 'resume',
  CoverLetter = 'cover letter',
  SelectionCriteria = 'selection criteria',
}

interface TrackLinkArgs {
  link: string;
  isExitLink?: boolean;
  linkName?: string; // Are links and linkName the same thing?
  eventName?: string | string[];
  pageTitle?: string;
  actionOrigin?: string;
  profileUpdateSource?: string;
  isCandidateNewToWorkforce?: boolean;
  profileVisibilityLevel?: string;
  previousProfileVisibility?: string;
  profileSharedStatus?: string;
  profileApproachabilityStatus?: string;
  tooltipType?: string;
  linkAction?: string;
  formField?: string | string[];
  currentPage?: string;
  currentPageSection?: string;
  currentPageSubsection?: string;
  locationType?: string;
  locationCountry?: string;
  updateType?: string;
  callToAction?: string;
  nudgeType?: string;
  personalDetailsSections?: string[];
  currentPageSectionState?: string;
  trackingCorrelationId?: string;
  linkText?: string;
  resumeHasSensitivePersonalInfo?: boolean;
  isResumeAttached?: boolean;
  isCoverLetterAttached?: boolean;
  jobBadges?: string[];
  jobBadgesShown?: string[];
  referencesSelection?: string;
  toggleState?: string;
  toggleType?: string;
  isStrongInterest?: boolean;
}

export type TrackLink = (
  payload: TrackLinkArgs &
    Partial<DocumentUploadErrorTracking> &
    Partial<TrackingLicenceAttributes> &
    Partial<TrackingLmisAttributes> &
    Partial<TrackingCertsyAttributes> &
    Partial<TrackingMostRecentRoleAttributes> &
    Partial<TrackingSimilarJobsAttributes> &
    Partial<TrackingSkillsAttributes> &
    Partial<TrackingCareerHistoryAttributes> &
    Partial<TrackingUnconfirmedDataAttributes> &
    Partial<TrackingDropdownResumeAttributes> &
    Partial<TrackingQualificationAttributes> &
    Partial<TrackingShowStrongInterestAttributes> &
    Partial<TrackingRecsImpression>,
) => void;

interface DocumentUploadErrorTracking {
  alertShownTone: string;
  alertShownType: string;
  alertShownPosition: string;
  alertShownMessage: string;
  alertShownMessageCode?: string;
  alertShownTitle?: string;
  documentId?: string;
  documentType: string;
  documentFormat: string;
  documentSize: number;
}

interface TrackViewArgs {
  view: string;
  eventName: string | string[];
  experiments?: Array<{ num: number; id: string; variant: number }>;
  actionOrigin?: string;
  profileUpdateSource?: string;
  experimentId?: string;
  skillsTotal?: number;
  currentPage?: string;
  currentPageSection?: string;
  locationType?: string;
  locationCountry?: string;
  updateType?: string;
  callToAction?: string;
  nudgeType?: string;
  profileVisibilityLevel?: string;
  profileSharedStatus?: string;
  profileApproachabilityStatus?: string;
  currentPageSectionState?: string;
  trackingCorrelationId?: string;
  resumeHasSensitivePersonalInfo?: boolean;
  isResumeAttached?: boolean;
  isCoverLetterAttached?: boolean;
}

interface TrackingDropdownResumeAttributes {
  resumeSelectedType:
    | 'preselected'
    | 'not-preselected'
    | 'dont-include'
    | 'upload'
    | 'do_not_include';
}

interface TrackingResumeAttributes {
  resumeIsPreselected?: boolean;
}

interface TrackingCareerHistoryAttributes {
  isDescriptionAdded: boolean;
}

interface TrackingMostRecentRoleAttributes {
  candidateHasRoles: boolean;
  recentRoleInteractionType: 'add' | 'edit' | 'no-edit' | 'no-role';
}

export interface TrackingSimilarJobsAttributes {
  recommendedJobPosition: number;
  numberOfRecommendedJobsDisplayed: number;
  recommendedJobId: string;
}

interface TrackingSkillsAttributes {
  skillsStartTotal: number;
  skillsCompleteTotal: number;
}

interface ParsedDataPoint {
  document: string;
  section: string;
  info: string;
  count: number;
}

interface TrackingUnconfirmedDataAttributes {
  parsedData: ParsedDataPoint[];
}

export const IMPRESSION_PROFILE_CERTSY = 'profile_certsy_viewed';

export enum TrackingVerificationStatus {
  VERIFIED = 'verified',
  NOT_VERIFIED = 'not-verified',
  UNVERIFIABLE = 'unverifiable',
}

export interface TrackingQualificationAttributes {
  certsyType: string;
  verificationStatus: TrackingVerificationStatus;
  qualificationsVerifiedTotal: number;
  qualificationsNotVerifiedTotal: number;
  qualificationsUnverifiableTotal: number;
}

interface TrackingRecsImpression {
  experimentFlightId: string;
}

interface TrackingShowStrongInterestAttributes {
  isPriorityApplyNudgeShown?: boolean;
  verifiedUser: boolean;
  jobId: string;
}

export type TrackView = (
  payload: TrackViewArgs &
    Partial<TrackingLicenceAttributes> &
    Partial<TrackingMostRecentRoleAttributes> &
    Partial<TrackingResumeAttributes> &
    Partial<TrackingShowStrongInterestAttributes> &
    Partial<TrackingQualificationAttributes>,
) => void;

export type UpdateAnalyticsState = (Args: {
  advertiserId: string;
  advertiserName: string;
  jobId: string;
  jobLocation?: string | null;
  jobLocationId?: string | null;
  jobArea?: string | null;
  jobAreaId?: string | null;
  jobClassification?: string | null;
  jobClassificationId?: string | null;
  jobSubClassification?: string | null;
  jobSubClassificationId?: string | null;
  jobHasScreen: false;
  jobIsLinkOut: boolean;
  isProfileApply: boolean;
  jobHasRoleRequirements: boolean;
  jobListingSource?: string | null;
  brand: Brand;
  siteCountry: Locale;
  siteLanguage: Language;
  zone: Zone;
  experimentsEligible?: TealiumExperimentProps[];
  hubbleExperiments?: HubbleExperiments;
}) => void;

export type HubbleEventName = Parameters<Hubble['trackEvent']>[0];

export interface Analytics {
  trackLink: TrackLink;
  trackView: TrackView;
  setPage: SetPage;
  updateAnalyticsState: UpdateAnalyticsState;
  trackingCorrelationId: (forHubbleEvent: HubbleEventName) => string;
}

export type CreateAnalytics = () => Analytics;

export interface AnalyticsState {
  profileGuid?: string;
  pageTitle?: string;
  currentPage?: string;
  page?: string;
  emailCorrelationId?: string;
  testingId?: string;
  experimentsEligible?: TealiumExperimentProps[];
  hubbleExperiments?: HubbleExperiments;
}
